import classNames from 'classnames';
import React from 'react';
import styled from 'styled-components';
import {
  COLOR,
  type ColorMode,
  fieldDisabledOpacity,
  fieldErrorShadow,
  fieldLabelFontSize,
  fieldLabelMargin,
  SIZE,
} from '../../styles';
import { FieldInfo } from '../FieldInfo';
import { bareInputStyle } from '../Input/Input';

const FieldSet: React.FC<{
  name: string;
  colorMode: ColorMode | undefined;
  className?: string;
  disabled?: boolean;
  label?: React.ReactNode;
  errorText?: React.ReactNode;
  error?: boolean;
  hintText?: React.ReactNode;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}> = ({
  name,
  colorMode,
  className,
  disabled,
  label,
  errorText,
  error,
  hintText,
  style,
  children,
}) => {
  const wrapperClass = classNames(className, {
    disabled,
    error: errorText || error,
  });

  return (
    <FieldSetWrapper className={wrapperClass} colorMode={colorMode} style={style}>
      {label && <label htmlFor={name}>{label}</label>}
      {children}
      {!errorText && hintText && <FieldInfo text={hintText} />}
      {errorText && <FieldInfo type="error" text={errorText} />}
    </FieldSetWrapper>
  );
};

const FieldSetWrapper = styled.fieldset<{ colorMode: ColorMode | undefined }>`
  border: none;
  display: block;
  font-family: inherit;
  margin: 0 0 ${SIZE['20']};
  padding: 0;
  position: relative;
  width: 100%;

  fieldset + &:last-child {
    margin-bottom: 0;
  }

  &,
  label,
  input {
    font-family: inherit;
    box-sizing: border-box;
    display: block;
    width: 100%;
  }

  input {
    ${(props) => bareInputStyle(props.colorMode)}
  }

  &.disabled {
    opacity: ${fieldDisabledOpacity};

    &,
    input:disabled,
    label {
      cursor: not-allowed;
    }

    input:disabled {
      background: ${COLOR['fieldDisabledBG']};
    }
  }

  &.error {
    input {
      border-color: ${COLOR['red-dark']};
      box-shadow: ${fieldErrorShadow};
    }

    label {
      color: ${COLOR['red-dark']};
    }
  }

  &.inline {
    label {
      width: auto;
    }
  }

  &:hover,
  &:focus {
    &:not(.disabled) {
      label {
        color: ${COLOR['primary']};
      }
    }
  }

  label {
    color: ${COLOR['fieldLabelColor']};
    cursor: pointer;
    font-size: ${fieldLabelFontSize};
    line-height: ${SIZE['16']};
    margin: ${fieldLabelMargin};
    padding: 0;

    &.error {
      color: ${COLOR['red-dark']};
    }

    &.mode-darker,
    &.mode-dark {
      color: ${COLOR['white']};

      &.error {
        color: ${COLOR['red']};
      }

      &.active:not(.disabled) {
        color: ${COLOR['turquoise']};
      }
    }

    &.disabled {
      cursor: not-allowed;
    }

    &.active:not(.disabled) {
      color: ${COLOR['primary']};
    }
  }
`;

export default FieldSet;
